import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  ExternalLink,
  Eye,
  Linkedin,
  MapPin,
  Megaphone,
  Trash2,
  Twitter,
} from "lucide-react";
import { toast } from "sonner";
import { raiseBoardApi } from "@/api/raise-board-api";
import { useDealsListStore } from "@/store/deals-list-store";
import { useWorkspaceSettings } from "@/hooks/use-workspace-settings";
import { useWorkspaceNavigation } from "@/hooks/use-workspace-navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getErrorMessage } from "@/lib/api-error";
import { getStageText, GICSIndustries, GICSSubIndustries } from "@/lib/utils";
import type { DealTransformed } from "@/lib/schema/deal-schema";
import { CompanyAvatar } from "./company-avatar";

function getCompanyLogo(deal: DealTransformed): string | undefined {
  return deal.company_files?.find((f) => f.file_type === "company avatar")?.file_url;
}

function getApprovedCountry(deal: DealTransformed): string | undefined {
  const fromDealInfo = deal.dealInfo?.deal?.find(
    (f) => f.field_name === "address"
  )?.field_value as string | undefined;
  return fromDealInfo || deal.overview?.country || undefined;
}

function getDealInfoString(deal: DealTransformed, fieldName: string): string | undefined {
  const v = deal.dealInfo?.deal?.find((f) => f.field_name === fieldName)?.field_value;
  if (typeof v === "string" && v.trim()) return v;
  if (Array.isArray(v) && v.length > 0) return v.filter(Boolean).join(", ");
  if (typeof v === "number") return String(v);
  return undefined;
}

function resolveGICSIndustries(deal: DealTransformed): { industry?: string; subIndustry?: string } {
  const sub = deal.dealInfo?.deal?.find(
    (f) => f.field_name === "sub_industries" && f.field_state === "approved"
  )?.field_value;
  const codes = Array.isArray(sub) ? sub.map(String) : sub ? [String(sub)] : [];
  const firstCode = codes.find((c) => !!c);
  if (!firstCode) {
    const top = deal.overview?.industry?.[0];
    return top ? { industry: top } : {};
  }
  const subIndustry = GICSSubIndustries.find((si) => si.code === firstCode)?.name;
  const parentCode = firstCode.length >= 8 ? firstCode.substring(0, 8) : undefined;
  const industry = parentCode ? GICSIndustries.find((i) => i.code === parentCode)?.name : undefined;
  return { industry, subIndustry };
}

export default function RaiseBoardPage() {
  const { navigate } = useWorkspaceNavigation();
  const {
    workspace,
    activeWorkspaceId,
    invalidateWorkspace,
    isLoading: isWorkspaceLoading,
  } = useWorkspaceSettings();
  const { deals, fetchDeals } = useDealsListStore();
  const isDealsLoading = useDealsListStore((s) => s.isLoading);
  const dealsLastFetch = useDealsListStore((s) => s.lastFetchTime);
  const [pendingRemove, setPendingRemove] = useState<{ id: string; name: string } | null>(null);
  const [viewing, setViewing] = useState<DealTransformed | null>(null);

  useEffect(() => {
    if (activeWorkspaceId) fetchDeals(activeWorkspaceId);
  }, [activeWorkspaceId, fetchDeals]);

  const isInitialLoad =
    isWorkspaceLoading || isDealsLoading || dealsLastFetch === null;

  const raiseBoardDeals = useMemo(
    () => Object.values(deals).filter((d) => d.raiseBoard === true),
    [deals]
  );

  const removeMutation = useMutation({
    mutationFn: async (companyId: string) => {
      if (!activeWorkspaceId) throw new Error("No workspace");
      return raiseBoardApi.bulkSet(activeWorkspaceId, [companyId], false);
    },
    onSuccess: async () => {
      if (activeWorkspaceId) await fetchDeals(activeWorkspaceId);
      toast.success("Removed from Raiseboard");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to remove from Raiseboard"));
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!activeWorkspaceId) throw new Error("No workspace");
      return raiseBoardApi.toggle(activeWorkspaceId, enabled);
    },
    onSuccess: () => {
      invalidateWorkspace();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to update Raiseboard"));
    },
  });

  const slug = workspace?.portal_slug;
  const enabled = workspace?.raise_board_enabled === true;
  const publicUrl = slug ? `${window.location.origin}/rb/${slug}` : null;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="flex items-center gap-3 font-serif text-3xl text-foreground sm:text-4xl">
            <Megaphone className="h-7 w-7 text-muted-foreground" />
            Raiseboard
          </h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground">
            Companies added here appear on your public Raiseboard for
            investors to discover and add to their pipeline.
          </p>
        </div>
        {isInitialLoad ? (
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-16 rounded-full" />
              <Skeleton className="h-9 w-20" />
            </div>
            <Skeleton className="h-3 w-48" />
          </div>
        ) : (
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="flex h-8 items-center rounded-sm border-border bg-card px-2.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground"
              >
                {enabled ? "Public" : "Disabled"}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => {
                  if (!slug) {
                    navigate("/settings/landing-page");
                    return;
                  }
                  toggleMutation.mutate(!enabled);
                }}
                disabled={toggleMutation.isPending}
              >
                {toggleMutation.isPending
                  ? "Updating..."
                  : !slug
                    ? "Set up"
                    : enabled
                      ? "Disable"
                      : "Enable"}
              </Button>
            </div>
            {publicUrl && enabled && (
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1 break-all text-xs text-muted-foreground hover:text-foreground"
              >
                {publicUrl}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
            {!slug && (
              <p className="text-xs text-muted-foreground">
                <button
                  type="button"
                  onClick={() => navigate("/settings/landing-page")}
                  className="underline"
                >
                  Set a portal URL
                </button>{" "}
                first to enable.
              </p>
            )}
          </div>
        )}
      </div>

      {isInitialLoad ? (
        <>
        <div className="space-y-3 md:hidden">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-lg border border-border bg-card p-4">
              <div className="flex items-start gap-3">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="mt-3 flex gap-2">
                <Skeleton className="h-8 flex-1" />
                <Skeleton className="h-8 flex-1" />
              </div>
            </div>
          ))}
        </div>
        <div className="hidden overflow-hidden rounded-lg border border-border md:block">
          <div className="grid grid-cols-[1fr_100px_160px_140px_44px_44px] items-center gap-4 border-b border-border bg-muted/40 px-4 py-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-16" />
            <span className="col-span-2 flex justify-center">
              <Skeleton className="h-3 w-14" />
            </span>
          </div>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="grid grid-cols-[1fr_100px_160px_140px_44px_44px] items-center gap-4 border-b border-border px-4 py-4 last:border-0"
            >
              <div className="flex min-w-0 items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-56" />
                </div>
              </div>
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-8 w-8 rounded-full" />
            </div>
          ))}
        </div>
        </>
      ) : raiseBoardDeals.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            No deals on the Raiseboard yet. Select deals on the main Deals
            page and use <span className="font-medium">Add to Raiseboard</span>.
          </CardContent>
        </Card>
      ) : (
        <>
        {/* Mobile cards */}
        <div className="space-y-3 md:hidden">
          {raiseBoardDeals.map((deal) => {
            const stageLabel =
              deal.overview?.companyStage != null
                ? getStageText(deal.overview.companyStage)
                : null;
            const industry = deal.overview?.industry?.[0];
            const country = getApprovedCountry(deal);

            return (
              <div
                key={deal.id}
                className="rounded-lg border border-border bg-card p-4"
              >
                <div className="flex items-start gap-3">
                  <CompanyAvatar
                    logoUrl={getCompanyLogo(deal)}
                    name={deal.overview?.name}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {deal.overview?.name || "Unnamed company"}
                    </p>
                    {deal.overview?.description && (
                      <p className="line-clamp-2 text-xs text-muted-foreground">
                        {deal.overview.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
                  {stageLabel && stageLabel !== "Unknown" && (
                    <Badge
                      variant="outline"
                      className="rounded-sm border-border bg-card text-[10px] font-semibold uppercase tracking-widest text-foreground"
                    >
                      {stageLabel}
                    </Badge>
                  )}
                  {industry && <span className="truncate">{industry}</span>}
                  {country && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 shrink-0" />
                      {country}
                    </span>
                  )}
                </div>
                <div className="mt-3 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setViewing(deal)}
                  >
                    <Eye className="mr-1.5 h-4 w-4" />
                    View
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 text-destructive hover:text-destructive"
                    onClick={() =>
                      setPendingRemove({
                        id: deal.id,
                        name: deal.overview?.name || "this company",
                      })
                    }
                    disabled={removeMutation.isPending}
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" />
                    Remove
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop table */}
        <div className="hidden overflow-hidden rounded-lg border border-border md:block">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_100px_160px_140px_44px_44px] items-center gap-4 border-b border-border bg-muted/40 px-4 py-3">
            {(["Startup", "Stage", "Industry", "Location"] as const).map(
              (col) => (
                <span
                  key={col}
                  className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground"
                >
                  {col}
                </span>
              )
            )}
            <span className="col-span-2 text-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Actions
            </span>
          </div>

          {/* Rows */}
          {raiseBoardDeals.map((deal) => {
            const stageLabel =
              deal.overview?.companyStage != null
                ? getStageText(deal.overview.companyStage)
                : null;
            const industry = deal.overview?.industry?.[0];

            return (
              <div
                key={deal.id}
                className="grid grid-cols-[1fr_100px_160px_140px_44px_44px] items-center gap-4 border-b border-border px-4 py-4 last:border-0 hover:bg-muted/20 transition-colors"
              >
                {/* Avatar + name + bio */}
                <div className="flex min-w-0 items-center gap-3">
                  <CompanyAvatar
                    logoUrl={getCompanyLogo(deal)}
                    name={deal.overview?.name}
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">
                      {deal.overview?.name || "Unnamed company"}
                    </p>
                    {deal.overview?.description && (
                      <p className="truncate text-xs text-muted-foreground">
                        {deal.overview.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Stage */}
                <div>
                  {stageLabel && stageLabel !== "Unknown" ? (
                    <Badge
                      variant="outline"
                      className="rounded-sm border-border bg-card text-[10px] font-semibold uppercase tracking-widest text-foreground"
                    >
                      {stageLabel}
                    </Badge>
                  ) : (
                    <span className="font-mono text-xs tabular-nums text-muted-foreground">—</span>
                  )}
                </div>

                {/* Industry */}
                <p className="truncate text-sm text-muted-foreground">
                  {industry ?? "—"}
                </p>

                {/* Location */}
                <div className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
                  {getApprovedCountry(deal) && (
                    <MapPin className="h-3 w-3 shrink-0" />
                  )}
                  <span className="truncate">
                    {getApprovedCountry(deal) ?? "—"}
                  </span>
                </div>

                {/* View details */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8 shrink-0 rounded-full"
                      onClick={() => setViewing(deal)}
                      aria-label="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>View details</TooltipContent>
                </Tooltip>

                {/* Remove */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8 shrink-0 rounded-full text-destructive hover:text-destructive"
                      onClick={() =>
                        setPendingRemove({
                          id: deal.id,
                          name: deal.overview?.name || "this company",
                        })
                      }
                      disabled={removeMutation.isPending}
                      aria-label="Remove from Raiseboard"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Remove from Raiseboard</TooltipContent>
                </Tooltip>
              </div>
            );
          })}
        </div>
        </>
      )}

      <DetailsDialog
        deal={viewing}
        onClose={() => setViewing(null)}
        onOpenInDeals={(dealId) => {
          setViewing(null);
          navigate(`/deals/${dealId}`);
        }}
      />

      <AlertDialog
        open={!!pendingRemove}
        onOpenChange={(open) => { if (!open) setPendingRemove(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from Raiseboard?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium">{pendingRemove?.name}</span> will
              no longer appear on your public Raiseboard.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingRemove) removeMutation.mutate(pendingRemove.id);
                setPendingRemove(null);
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </TooltipProvider>
  );
}

function DetailsDialog({
  deal,
  onClose,
  onOpenInDeals,
}: {
  deal: DealTransformed | null;
  onClose: () => void;
  onOpenInDeals: (dealId: string) => void;
}) {
  if (!deal) return null;
  const overview = deal.overview;
  const name = overview?.name ?? "Untitled";
  const bio = overview?.description ?? undefined;
  const stageLabel =
    overview?.companyStage != null ? getStageText(overview.companyStage) : null;
  const { industry, subIndustry } = resolveGICSIndustries(deal);
  const country = getApprovedCountry(deal);
  const founded = overview?.founded ?? undefined;
  const businessModel = overview?.businessModel ?? undefined;
  const revenueModel = getDealInfoString(deal, "revenue_models");
  const raise = (() => {
    const amount = getDealInfoString(deal, "attempting_to_raise");
    const ccy = getDealInfoString(deal, "currency");
    if (!amount) return null;
    return ccy ? `${amount} ${ccy}` : amount;
  })();

  const website = overview?.website ?? undefined;
  const websiteHost = website
    ? website.replace(/^https?:\/\//, "").replace(/\/$/, "")
    : null;
  const websiteHref = website
    ? website.startsWith("http") ? website : `https://${website}`
    : null;

  const linkedin = getDealInfoString(deal, "linkedIn");
  const twitter = getDealInfoString(deal, "twitter");

  const objective = getDealInfoString(deal, "objective");
  const problem = getDealInfoString(deal, "problem");
  const solution = getDealInfoString(deal, "solution");
  const traction = getDealInfoString(deal, "traction");

  const stats: Array<{ label: string; value: string }> = [];
  if (stageLabel && stageLabel !== "Unknown") stats.push({ label: "Stage", value: stageLabel });
  if (raise) stats.push({ label: "Target raise", value: raise });
  if (country) stats.push({ label: "Country", value: country });
  if (founded) stats.push({ label: "Founded", value: founded });
  if (industry) stats.push({ label: "Industry", value: industry });
  if (subIndustry && subIndustry !== industry) stats.push({ label: "Sub-industry", value: subIndustry });
  if (businessModel) stats.push({ label: "Business model", value: businessModel });
  if (revenueModel) stats.push({ label: "Revenue model", value: revenueModel });

  const pitchSections: Array<{ label: string; body: string }> = [];
  if (objective) pitchSections.push({ label: "Objective", body: objective });
  if (problem) pitchSections.push({ label: "Problem", body: problem });
  if (solution) pitchSections.push({ label: "Solution", body: solution });
  if (traction) pitchSections.push({ label: "Traction", body: traction });

  const links: Array<{ label: string; href: string; icon: typeof Linkedin }> = [];
  if (linkedin)
    links.push({
      label: "LinkedIn",
      href: linkedin.startsWith("http") ? linkedin : `https://${linkedin}`,
      icon: Linkedin,
    });
  if (twitter)
    links.push({
      label: "Twitter",
      href: twitter.startsWith("http") ? twitter : `https://${twitter}`,
      icon: Twitter,
    });

  return (
    <Dialog open={!!deal} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="grid max-h-[88vh] max-w-2xl grid-rows-[1fr_auto] overflow-hidden p-0 gap-0">
        {/* Scrollable body */}
        <div className="min-h-0 overflow-y-auto">
          {/* Header */}
          <div className="flex items-start gap-4 px-6 pt-6 pb-5 pr-12">
            <CompanyAvatar
              logoUrl={getCompanyLogo(deal)}
              name={overview?.name}
              size="lg"
            />
            <div className="min-w-0 flex-1">
              <DialogTitle className="font-serif text-2xl leading-snug text-foreground sm:text-3xl">
                {name}
              </DialogTitle>
              {(websiteHost || links.length > 0) && (
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  {websiteHost && websiteHref && (
                    <a
                      href={websiteHref}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {websiteHost}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                  {links.map(({ label, href, icon: Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                      className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bio */}
          {bio && (
            <div className="px-6 pb-4">
              <p className="text-sm leading-relaxed text-foreground/80">{bio}</p>
            </div>
          )}

          {/* Stats */}
          {stats.length > 0 && (
            <div className="border-t border-border bg-muted/20 px-6 py-5">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                {stats.map((s) => {
                  const isNumeric =
                    s.label === "Target raise" || s.label === "Founded";
                  return (
                    <div key={s.label}>
                      <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                        {s.label}
                      </p>
                      <p
                        className={`mt-1 text-sm font-medium text-foreground ${
                          isNumeric ? "font-mono tabular-nums" : ""
                        }`}
                      >
                        {s.value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pitch */}
          {pitchSections.length > 0 && (
            <div className="border-t border-border px-6 py-5 space-y-5">
              {pitchSections.map((s) => (
                <section key={s.label}>
                  <h4 className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {s.label}
                  </h4>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground/85 whitespace-pre-line">
                    {s.body}
                  </p>
                </section>
              ))}
            </div>
          )}

        </div>

        {/* Sticky footer */}
        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-3 sm:px-6">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="hidden sm:inline-flex"
          >
            Close
          </Button>
          <Button
            size="sm"
            onClick={() => onOpenInDeals(deal.id)}
            className="w-full sm:w-auto"
          >
            Open in Deals
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
